import { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../../components/app/AppToast";
import SaveHeader from "../../../components/app/headers/SaveHeader";
import CustomIcon from "../../../components/CustomIcon";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import Loader from "../../../components/Loader";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import ProfilePicture from "../../../components/ProfilePicture";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import {
  apiClient,
  revalidateApiQuery,
  useApiQuery,
} from "../../../services/api";
import type { Api } from "../../../services/api";
import {
  HIDDEN_SPECIALTY_IDS,
  UNAVAILABLE_SPECIALTIES,
} from "../../../tools/utils";
import ScrollableLayout from "../../../components/layout/ScrollableLayout";
import { afterMainSpecialtySaved } from "../../../domains/user/saveMainSpecialty";
import { openSettingsAt } from "../../../domains/app/stores/settingsSheetStore";
import { useTranslation } from "react-i18next";

type Profession =
  Api["get"]["/webapp/public/professions"]["output"]["body"][number];
type Tag = Api["get"]["/webapp/public/tags/parents"]["output"]["body"][number];

function getLostSpecialtiesForProfession(input: {
  profession: Profession | null;
  selectedMainSpecialty: Tag | null;
  selectedOtherSpecialties: Tag[];
}) {
  if (input.profession === null) return [];

  const selectedSpecialties = [
    input.selectedMainSpecialty,
    ...input.selectedOtherSpecialties,
  ].filter((specialty) => specialty !== null);
  const professionSpecialtyIds = new Set(input.profession.medical_specialties);

  return selectedSpecialties.filter((specialty) => {
    return !professionSpecialtyIds.has(specialty.id);
  });
}

export default function EditProfilePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser, isOnboarded } = useCurrentUser();
  const professionsQuery = useApiQuery("/webapp/public/professions", {
    durable: true,
  });
  const allSpecialtiesQuery = useApiQuery("/webapp/public/tags/parents", {
    durable: true,
  });
  const professions =
    professionsQuery.state === "success" ? professionsQuery.body : [];
  const allSpecialties =
    allSpecialtiesQuery.state === "success" ? allSpecialtiesQuery.body : [];
  const isLoading =
    !currentUser ||
    !isOnboarded ||
    professionsQuery.state === "pending" ||
    allSpecialtiesQuery.state === "pending";
  const sortedProfessions = [...professions].sort((a, b) => {
    return getItemTranslation(a).localeCompare(getItemTranslation(b));
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [link, setLink] = useState("");
  const [bio, setBio] = useState("");
  const [selectedProfession, setSelectedProfession] =
    useState<Profession | null>(null);
  const [pendingProfession, setPendingProfession] = useState<Profession | null>(
    null
  );
  const [selectedMainSpecialty, setSelectedMainSpecialty] =
    useState<Tag | null>(null);
  const [selectedOtherSpecialties, setSelectedOtherSpecialties] = useState<
    Tag[]
  >([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [needSaving, setNeedSaving] = useState(false);
  const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);
  const [
    lostSpecialtiesOnProfessionChange,
    setLostSpecialtiesOnProfessionChange,
  ] = useState<Tag[]>([]);

  useEffect(() => {
    if (currentUser && isOnboarded) {
      setFirstName(currentUser.firstname ?? "");
      setLastName(currentUser.lastname ?? "");
      setLink(currentUser.link ?? "");
      setBio(currentUser.bio ?? "");
      // NOTE: all instances of "workplace/location" are commented until reconsideration
      // setWorkplace(currentUser.location);
      const matchedProfession =
        professions.find((p) => p.uid === currentUser.professionModel?.uid) ??
        null;
      setSelectedProfession(matchedProfession);
      setSelectedMainSpecialty(currentUser.mainSpecialtyModel);
      setSelectedOtherSpecialties(currentUser.medicalSpecialtyModels);
      setSelectedTags(currentUser.tagModels);
      setPendingProfession(null);
      setLostSpecialtiesOnProfessionChange([]);
    }
  }, [currentUser, isOnboarded, professions]);

  useEffect(() => {
    let changed = false;
    if (!isLoading && currentUser && isOnboarded) {
      if (firstName !== (currentUser.firstname ?? "")) changed = true;
      if (lastName !== (currentUser.lastname ?? "")) changed = true;
      if (link !== (currentUser.link ?? "")) changed = true;
      if (bio !== (currentUser.bio ?? "")) changed = true;
      // if (workplace !== currentUser.location) changed = true;
      if (selectedProfession?.uid !== currentUser.professionModel?.uid)
        changed = true;
      if (selectedMainSpecialty?.uid !== currentUser.mainSpecialtyModel?.uid)
        changed = true;
      if (
        selectedOtherSpecialties.map((s) => s.uid).join("") !==
        currentUser.medicalSpecialtyModels.map((s) => s.uid).join("")
      )
        changed = true;
      if (
        selectedTags.map((s: { uid: string }) => s.uid).join("") !==
        currentUser.tagModels.map((s) => s.uid).join("")
      )
        changed = true;
    }
    setNeedSaving(changed);
  }, [
    isLoading,
    firstName,
    lastName,
    link,
    bio,
    // workplace,
    selectedProfession?.uid,
    selectedMainSpecialty?.uid,
    selectedOtherSpecialties,
    selectedTags,
  ]);

  function getAvailableSpecialties(
    profession: Profession | null = selectedProfession
  ) {
    if (profession === null) return [];
    const profSpecIds = new Set(profession.medical_specialties);
    const alreadyUsed = [selectedMainSpecialty, ...selectedOtherSpecialties];
    return allSpecialties
      .filter((spe) => profSpecIds.has(spe.id))
      .filter((spe) => !HIDDEN_SPECIALTY_IDS.has(spe.id))
      .filter((spe) => !alreadyUsed.some((u) => u?.uid === spe.uid))
      .filter(
        (spe) =>
          !UNAVAILABLE_SPECIALTIES.includes(getItemTranslation(spe, "en"))
      );
  }

  async function handleChangeProfession(option: { value: string }) {
    const newProfession = professions.find((el) => el.uid === option.value);
    if (newProfession === undefined) {
      console.error("No such profession", option.value);
      return;
    }

    const currentProfessionUid =
      selectedProfession === null ? null : selectedProfession.uid;
    if (newProfession.uid === currentProfessionUid) {
      setPendingProfession(null);
      setLostSpecialtiesOnProfessionChange([]);
      return;
    }

    const nextLostSpecialties = getLostSpecialtiesForProfession({
      profession: newProfession,
      selectedMainSpecialty,
      selectedOtherSpecialties,
    });

    if (nextLostSpecialties.length === 0) {
      setSelectedProfession(newProfession);
      setPendingProfession(null);
      setLostSpecialtiesOnProfessionChange([]);
      return;
    }

    setPendingProfession(newProfession);
    setLostSpecialtiesOnProfessionChange(nextLostSpecialties);
  }

  function handleConfirmProfessionChange() {
    if (pendingProfession === null) {
      throw new Error(
        "should not happen: profession change was confirmed without a pending profession"
      );
    }

    const selectedMainSpecialtyUid =
      selectedMainSpecialty === null ? null : selectedMainSpecialty.uid;

    // Replace main specialty if incompatible
    if (
      lostSpecialtiesOnProfessionChange.find(
        (spe) => spe.uid === selectedMainSpecialtyUid
      )
    ) {
      const availableSpecialties = getAvailableSpecialties(pendingProfession);
      const nextMainSpecialty = availableSpecialties[0];
      setSelectedMainSpecialty(
        nextMainSpecialty === undefined ? null : nextMainSpecialty
      );
    }

    // Remove other specialties if incompatible
    setSelectedOtherSpecialties(
      selectedOtherSpecialties.filter((spe) => {
        return !lostSpecialtiesOnProfessionChange.some(
          (lostSpe) => lostSpe.uid === spe.uid
        );
      })
    );

    // Remove tags from lost specialties
    setSelectedTags(
      selectedTags.filter((tag) => {
        // NOTE: Temp fix because of bad populate. Parent uid check should be enough.
        if (tag.parent === null || tag.parent === undefined) return true;
        const lostSpecialtiesIds = lostSpecialtiesOnProfessionChange.map(
          (spe) => spe.id
        );
        return !lostSpecialtiesIds.includes(tag.parent);
      })
    );

    setSelectedProfession(pendingProfession);
    setPendingProfession(null);
    setLostSpecialtiesOnProfessionChange([]);
  }

  function handleCancelProfessionChange() {
    setPendingProfession(null);
    setLostSpecialtiesOnProfessionChange([]);
  }

  function formatLink(inputLink: string): string | undefined {
    if (inputLink === "") return undefined;
    if (inputLink.startsWith("http://") || inputLink.startsWith("https://")) {
      return inputLink;
    }
    return `https://${inputLink}`;
  }

  async function handleSubmit() {
    try {
      const formattedLink = formatLink(link);

      const urlRegex = String.raw`^(?:(?:http|https)://)(?:\S+(?::\S*)?@)?(?:(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[0-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]+-?)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]+-?)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))|localhost)(?::\d{2,5})?(?:(/|\?|#)[^\s]*)?$`;

      if (
        formattedLink !== undefined &&
        !new RegExp(urlRegex, "i").test(formattedLink)
      ) {
        displayToast(t("Please provide a valid link."), "warning");
        return;
      }

      const previousSpecialty =
        currentUser === null || currentUser.mainSpecialtyModel === null
          ? null
          : currentUser.mainSpecialtyModel.id;
      // A new main specialty makes the server purge the other specialties and
      // the tags of the old one, so this form does not send them back with it.
      // One PATCH: the profile fields and the specialty land or fail together.
      const specialtyChange =
        previousSpecialty === null ||
        selectedMainSpecialty === null ||
        selectedMainSpecialty.id === previousSpecialty
          ? null
          : { from: previousSpecialty, to: selectedMainSpecialty.id };
      await apiClient.patch("/webapp/private/users/me", {
        json: {
          firstname: firstName,
          lastname: lastName,
          bio,
          // location: workplace,
          link: formattedLink,
          profession: selectedProfession?.id,
          main_specialty: selectedMainSpecialty?.id,
          medical_specialties:
            specialtyChange === null
              ? selectedOtherSpecialties.map((tag) => tag.id)
              : undefined,
          tags:
            specialtyChange === null
              ? selectedTags.map((tag) => tag.id)
              : undefined,
        },
      });
      if (specialtyChange === null) {
        void revalidateApiQuery("/webapp/private/playlists/recommended");
        reloadCurrentUser();
      } else {
        await afterMainSpecialtySaved(specialtyChange);
      }

      displayToast(t("Profile updated"), "success");
      history.goBack();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <SaveHeader
          title={t("Edit your profile")}
          saveReady={needSaving}
          onSaveClick={handleSubmit}
          onGoBack={() => {
            if (needSaving) {
              setConfirmModalVisible(true);
            } else {
              history.goBack();
            }
          }}
        />
      }
    >
      {isLoading ? (
        <Loader expanded />
      ) : (
        // TODO: rework all the JSX
        <div style={{ minHeight: window.innerHeight * 0.8 }}>
          <EditAvatar
            onClick={() => {
              history.push("/settings/edit/profile/avatar");
            }}
          />

          <div className="p-[16px_40px]">
            <h2 className="font-bold text-[21px] leading-[30px] tracking-[0.02em] text-primary-black">
              {t("Main information")}
            </h2>

            <Input
              value={firstName}
              onInput={(e: ChangeEvent<HTMLInputElement>) => {
                setFirstName(e.target.value);
              }}
              placeholder={t("First Name")}
            />
            <div style={{ height: 15 }} />

            <Input
              value={lastName}
              onInput={(e: ChangeEvent<HTMLInputElement>) => {
                setLastName(e.target.value);
              }}
              placeholder={t("Last Name")}
            />
            <div style={{ height: 15 }} />

            <Select
              appearance="field"
              footerAction={null}
              searchOnlyOptions={null}
              value={selectedProfession?.uid ?? ""}
              options={sortedProfessions.map((el) => ({
                value: el.uid,
                label: getItemTranslation(el),
              }))}
              placeholder="Profession"
              onChange={handleChangeProfession}
              className="bg-white"
            />

            <div style={{ height: 15 }} />

            <FakeSelect
              label={t("Main specialty")}
              content={
                selectedMainSpecialty
                  ? getItemTranslation(selectedMainSpecialty)
                  : ""
              }
              onClick={() => {
                openSettingsAt({ view: "mainSpecialty" });
              }}
            />

            <div style={{ height: 8 }} />

            <p className="font-medium text-[16px] leading-[110%] tracking-[0.02em] text-secondary-bluegrey-6 m-[24px_0_16px] disclaimer">
              {t("Highlight your web space")}
            </p>
            <Input
              value={link}
              onInput={(e: ChangeEvent<HTMLInputElement>) => {
                setLink(e.target.value.toLowerCase());
              }}
              placeholder="example.com"
            />
            <div style={{ height: 20 }} />

            <textarea
              value={bio}
              onInput={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setBio(e.target.value);
              }}
              placeholder={t("Update your bio")}
              rows={5}
              className="w-full rounded-xl border border-solid border-[rgba(0,0,0,0.25)] bg-[rgba(255,255,255,0.85)] shadow-[0px_2px_8px_rgba(0,0,0,0.08)] px-4 py-3 font-dm-sans text-[15px] leading-[1.4] text-[#2d2d2d] placeholder:text-[rgba(122,109,101,0.5)] focus:outline-none focus:border-[rgba(0,0,0,0.4)] resize-none"
            />
            <div style={{ height: 20 }} />
          </div>
          <div style={{ height: 100 }} />
        </div>
      )}

      <ConfirmModal
        isVisible={isConfirmModalVisible}
        promptText={t(
          "Are you sure you want to exit without saving your changes?"
        )}
        onConfirm={() => {
          history.replace("/settings");
        }}
        onCancel={() => {
          setConfirmModalVisible(false);
        }}
      />
      <ConfirmModal
        isVisible={
          pendingProfession !== null &&
          lostSpecialtiesOnProfessionChange.length > 0
        }
        promptText={t(
          "Selecting this profession will change some of your specialties. Are you sure?"
        )}
        onConfirm={handleConfirmProfessionChange}
        onCancel={handleCancelProfessionChange}
      />
    </ScrollableLayout>
  );
}

function EditAvatar({ onClick }: { onClick: () => void }) {
  const { currentUser } = useCurrentUser();
  if (!currentUser) return null;
  return (
    <div
      className="mx-auto mt-5 w-fit cursor-pointer"
      onClick={() => {
        onClick();
      }}
    >
      <ProfilePicture
        height={90}
        user={currentUser}
        badge={<CustomIcon iconName="edit_avatar" />}
      />
    </div>
  );
}

function FakeSelect({
  onClick,
  label,
  content,
}: {
  onClick: () => void;
  label: string;
  content: string;
}) {
  return (
    <div
      className="flex items-center bg-white rounded-[5px] p-[8px_12px] cursor-pointer"
      onClick={() => {
        onClick();
      }}
    >
      <div className="w-full">
        <div className="text-secondary-placeholder text-[12px] font-medium m-[0_0_4px]">
          {label}
        </div>
        <div className="text-primary-black text-[16px] font-medium">
          {content}
        </div>
      </div>
      <CustomIcon scale={0.8} iconName="select-arrow" />
    </div>
  );
}
